/**
 * JustPluckingAround — the LIVE half of the Social split.
 *
 * The Elfsight `#PooPluckers` photo feed is the STAR of this page:
 * it renders in HERO position at the top of the layout, above every
 * other content block. When the env-configured widget ID is absent
 * (dev / preview without the widget wired), the page shows a small
 * fallback hero band and still renders the follow-us row + gallery
 * below so the page isn't empty.
 *
 * Content order:
 *   1. ElfsightSocialFeed (hero) — renders when the widget env is
 *      set; otherwise renders null.
 *   2. Fallback hero band — renders only when the widget env is
 *      absent, so the page always has something at the top.
 *   3. FollowUsSection — Facebook + Instagram pills + tag prompt.
 *   4. PluckinAroundGallery — admin-curated masonry.
 *
 * Route: /social/just-plucking-around. Legacy /social redirects here.
 *
 * Scope guardrail: the full social-platform deliverable described in
 * `/app/memory/social_step0_investigation.md` (3 new DB collections,
 * UGC posts, moderation pipeline, object-storage avatars) stays
 * fully deferred. This page is frontend-only, admin-curated, no DB.
 */
import React, { useEffect } from "react";
import { Facebook, Instagram } from "lucide-react";
import PublicPageShell from "@/components/PublicPageShell";
import PluckinAroundGallery from "@/components/social/PluckinAroundGallery";
import {
  JPA_HERO,
  SOCIAL_LINKS,
  SOCIAL_FOLLOW_HEADING,
} from "@/content/social";

// Elfsight platform.js source URL — used by ElfsightSocialFeed. Kept
// as a module constant so tests can grep for it in `document.head`
// without repeating the literal.
const ELFSIGHT_SCRIPT_SRC = "https://elfsightcdn.com/platform.js";

// String-keyed icon dereference. Keeping SOCIAL_LINKS in
// `content/social.js` dependency-free of `lucide-react` (see comment
// there) means the lookup lives here.
const ICON_MAP = {
  Facebook,
  Instagram,
};

/**
 * Pill-shaped outline link. Brand-token-driven so light + dark mode
 * both pass AA. Uses pure brand tokens — no invented colors.
 */
function FollowLink({ url, iconName, ariaLabel, platform, testId }) {
  const Icon = ICON_MAP[iconName];
  if (!Icon) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      data-testid={testId}
      className="
        group inline-flex items-center gap-2 rounded-full
        border-2 px-5 py-2.5
        text-[15px] font-semibold
        cursor-pointer transition-colors
      "
      style={{
        borderColor: "var(--pp-slate)",
        color: "var(--pp-slate)",
        background: "transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--pp-orange)";
        e.currentTarget.style.color = "var(--pp-orange)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--pp-slate)";
        e.currentTarget.style.color = "var(--pp-slate)";
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "var(--pp-orange)";
        e.currentTarget.style.color = "var(--pp-orange)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "var(--pp-slate)";
        e.currentTarget.style.color = "var(--pp-slate)";
      }}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
      <span>{platform}</span>
    </a>
  );
}

function FollowUsSection() {
  if (!SOCIAL_LINKS || SOCIAL_LINKS.length === 0) return null;
  return (
    <section data-testid="social-follow-us-section" aria-labelledby="follow-us-heading">
      <div className="pp-wrap pb-8 md:pb-12 text-center">
        <p
          id="follow-us-heading"
          className="text-[13px] font-bold uppercase tracking-[0.08em]"
          style={{ color: "var(--pp-orange)" }}
          data-testid="social-follow-us-eyebrow"
        >
          {SOCIAL_FOLLOW_HEADING.eyebrow}
        </p>
        <p
          className="mt-3 text-[15px] leading-relaxed mx-auto max-w-xl"
          style={{ color: "var(--pp-text-soft)" }}
          data-testid="social-follow-us-copy"
        >
          {SOCIAL_FOLLOW_HEADING.copy}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {SOCIAL_LINKS.map((link) => (
            <FollowLink key={link.platform} {...link} />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * ElfsightSocialFeed — HERO section for /social/just-plucking-around.
 *
 * Renders as the FIRST content block on the page when the public
 * widget ID env var is set; renders null otherwise. Route-scoped
 * script injection is idempotent and never removed on unmount
 * (other Elfsight widgets may still need the shared loader).
 *
 * Env-driven design:
 *   - `REACT_APP_ELFSIGHT_SOCIAL_WIDGET_ID` is a PUBLIC client-side
 *     ID — widget IDs are inherently visible in the rendered DOM
 *     (Elfsight designed them that way). Env-configured for
 *     swappability, NOT secrecy.
 *   - Widget ID value is NEVER hardcoded here.
 */
function ElfsightSocialFeed() {
  const widgetId = process.env.REACT_APP_ELFSIGHT_SOCIAL_WIDGET_ID;

  useEffect(() => {
    if (!widgetId) return;
    const existing = document.head.querySelector(
      `script[src="${ELFSIGHT_SCRIPT_SRC}"]`,
    );
    if (existing) return;
    const script = document.createElement("script");
    script.src = ELFSIGHT_SCRIPT_SRC;
    script.async = true;
    script.setAttribute("data-elfsight-loader", "social-page");
    document.head.appendChild(script);
    // NOTE: no cleanup function. Removing the script on unmount
    // would break other Elfsight widgets rendered later in the
    // same session.
  }, [widgetId]);

  if (!widgetId) return null;

  return (
    <section
      data-testid="social-elfsight-section"
      aria-labelledby="social-elfsight-heading"
    >
      <div className="pp-wrap pt-12 md:pt-20 pb-12 md:pb-20 text-center">
        <h1
          id="social-elfsight-heading"
          className="pp-h1"
          data-testid="social-elfsight-heading"
        >
          The pack, live.
        </h1>
        <p
          className="mt-3 text-[15px] leading-relaxed mx-auto max-w-xl"
          style={{ color: "var(--pp-text-soft)" }}
          data-testid="social-elfsight-subline"
        >
          Tag #PooPluckers to end up here.
        </p>
        <div
          className={`elfsight-app-${widgetId} mt-8`}
          data-elfsight-app-lazy
          data-testid="social-elfsight-container"
        />
      </div>
    </section>
  );
}

/**
 * FallbackHeroBand — small branded band rendered ONLY when the
 * Elfsight env var is absent, so the page still has a top-of-page
 * hero. Provides the same eyebrow + headline shape the rest of the
 * marketing site uses so the page doesn't feel empty.
 */
function FallbackHeroBand({ show }) {
  if (!show) return null;
  return (
    <section
      data-testid="jpa-fallback-hero-section"
      aria-labelledby="jpa-fallback-hero-headline"
    >
      <div className="pp-wrap py-12 md:py-20 text-center">
        <span className="pp-eyebrow" data-testid="jpa-fallback-hero-eyebrow">
          <span className="dot" />
          {JPA_HERO.eyebrow}
        </span>
        <h1
          id="jpa-fallback-hero-headline"
          className="pp-h1 mt-6 mx-auto max-w-3xl"
          data-testid="jpa-fallback-hero-headline"
        >
          {JPA_HERO.headline}
        </h1>
        <p
          className="pp-sub mt-5 mx-auto max-w-2xl"
          data-testid="jpa-fallback-hero-intro"
        >
          {JPA_HERO.intro}
        </p>
      </div>
    </section>
  );
}

export default function JustPluckingAround() {
  const widgetId = process.env.REACT_APP_ELFSIGHT_SOCIAL_WIDGET_ID;
  const hasWidget = Boolean(widgetId);
  return (
    <PublicPageShell>
      {/* Elfsight widget in HERO position — first content block. */}
      <ElfsightSocialFeed />
      {/* Fallback hero only when the widget is not wired. */}
      <FallbackHeroBand show={!hasWidget} />
      <FollowUsSection />
      <PluckinAroundGallery />
    </PublicPageShell>
  );
}
